/**
 * AppScrollArea — Reusable container with custom styled scrollbar + optional lazy loading.
 *
 * Hides the native scrollbar and renders a thin custom thumb/track overlay.
 * Only shows the scrollbar when content overflows the maxHeight.
 *
 * Props:
 *  maxHeight    - CSS max-height string (e.g. '430px', '20rem')
 *  className    - additional classes on the outer wrapper
 *  children     - scrollable content
 *  onLoadMore   - callback fired when scroll sentinel enters viewport (lazy loading)
 *  isLoading    - show loading spinner at bottom when true
 *  hasMore      - whether more items can be loaded
 */

import { useEffect, useRef, useState } from 'react'

export default function AppScrollArea({
  maxHeight = '240px',
  className = '',
  children,
  onLoadMore,
  isLoading = false,
  hasMore = false,
}) {
  const listRef = useRef(null)
  const trackRef = useRef(null)
  const thumbRef = useRef(null)
  const sentinelRef = useRef(null)
  const observerRef = useRef(null)
  const [overflows, setOverflows] = useState(false)

  // ── Detect overflow ─────────────────────────────────────────────────────
  useEffect(() => {
    const list = listRef.current
    if (!list) return
    const check = () => setOverflows(list.scrollHeight > list.clientHeight)
    check()
    const observer = new ResizeObserver(check)
    observer.observe(list)
    return () => observer.disconnect()
  }, [children])

  // ── Lazy loading via IntersectionObserver ────────────────────────────────
  useEffect(() => {
    if (!onLoadMore || !hasMore) return
    if (observerRef.current) observerRef.current.disconnect()

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasMore && !isLoading) {
          onLoadMore()
        }
      },
      { threshold: 0.1 },
    )

    const sentinel = sentinelRef.current
    if (sentinel) observerRef.current.observe(sentinel)
    return () => observerRef.current?.disconnect()
  }, [onLoadMore, hasMore, isLoading])

  // ── Custom scrollbar sync ───────────────────────────────────────────────
  useEffect(() => {
    const list = listRef.current
    const thumb = thumbRef.current
    const track = trackRef.current
    if (!list || !thumb || !track || !overflows) return

    function syncThumb() {
      const { scrollTop, scrollHeight, clientHeight } = list
      if (scrollHeight <= clientHeight) return
      const trackH = track.clientHeight
      const thumbH = Math.max(24, (clientHeight / scrollHeight) * trackH)
      const maxTop = trackH - thumbH
      const top = scrollHeight - clientHeight > 0
        ? (scrollTop / (scrollHeight - clientHeight)) * maxTop
        : 0
      thumb.style.height = `${thumbH}px`
      thumb.style.transform = `translateY(${top}px)`
    }

    syncThumb()
    list.addEventListener('scroll', syncThumb, { passive: true })
    return () => list.removeEventListener('scroll', syncThumb)
  }, [overflows, children])

  return (
    <div className={`relative ${className}`}>
      <div
        ref={listRef}
        className="app-dropdown-list"
        style={{
          maxHeight,
          overflowY: overflows ? 'scroll' : 'visible',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
        }}
      >
        {children}

        {/* Sentinel for infinite scroll */}
        {onLoadMore ? <div ref={sentinelRef} style={{ height: 1 }} /> : null}

        {/* Loading spinner */}
        {isLoading ? (
          <div className="flex items-center justify-center py-3">
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          </div>
        ) : null}
      </div>

      {/* Custom scrollbar track + thumb */}
      {overflows ? (
        <div
          ref={trackRef}
          className="pointer-events-none absolute right-[3px] bottom-1 top-1 w-[3px] rounded-full bg-outline-variant/20"
        >
          <div
            ref={thumbRef}
            className="absolute right-0 top-0 w-full rounded-full bg-primary/40 transition-opacity"
          />
        </div>
      ) : null}
    </div>
  )
}
